import type { ApiRoutes } from '~/api/pipelineV2';

const API_FIRST = 'api-first:';

type BlockLike = {
  stepName?: unknown;
};

type ParsedRouteRow = {
  path: string;
  methodId: string;
  method: string;
};

function isApiFirstRouteTrackKey(key: string): boolean {
  if (!key.startsWith(API_FIRST)) {
    return false;
  }
  if (
    key.startsWith(`${API_FIRST}choice:`) ||
    key.startsWith(`${API_FIRST}not-found:`)
  ) {
    return false;
  }
  if (key.endsWith('-onProcessTrack') || key.endsWith('-onExceptionTrack')) {
    return false;
  }
  if (key.includes(':success-response:') || key.includes(':error-response:')) {
    return false;
  }

  return true;
}

function parseBlockExecutionStepName(stepName: string): {
  method: string;
  path: string;
} | null {
  const trimmed = stepName.trim();
  const close = trimmed.indexOf(')');
  if (close < 2 || trimmed[0] !== '(') {
    return null;
  }
  const method = trimmed.slice(1, close).trim();
  const pathPart = trimmed.slice(close + 1).trim();
  if (!method || !pathPart) {
    return null;
  }

  let path = pathPart;
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  return { method: method.toUpperCase(), path };
}

function firstBlockSpec(trackVal: unknown): BlockLike | null {
  if (!Array.isArray(trackVal) || trackVal.length === 0) {
    return null;
  }
  const first = trackVal[0];
  if (!first || typeof first !== 'object') {
    return null;
  }
  return first as BlockLike;
}

function parseRowFromTrack(
  spec: Record<string, unknown>,
  key: string
): ParsedRouteRow | null {
  const methodId = key.slice(API_FIRST.length).trim();
  if (!methodId) {
    return null;
  }
  const block = firstBlockSpec(spec[key]);
  const stepName = typeof block?.stepName === 'string' ? block.stepName : null;
  if (!stepName) {
    return null;
  }
  const parsed = parseBlockExecutionStepName(stepName);
  if (!parsed) {
    return null;
  }
  return {
    path: parsed.path,
    methodId,
    method: parsed.method
  };
}

/**
 * Rebuilds `restApiTriggerRoutes` from an api-first `flowSpec` when the pipeline
 * document has empty/missing `restApiTriggerRoutes` but the canvas already shows
 * Block-Execution rows (`(METHOD) /path` stepName on route tracks `api-first:{methodId}`).
 */
export function deriveRestApiTriggerRoutesFromFlowSpec(
  flowSpec: unknown
): ApiRoutes[] | null {
  if (!flowSpec || typeof flowSpec !== 'object') {
    return null;
  }

  const spec = flowSpec as Record<string, unknown>;

  const rows = Object.keys(spec)
    .filter(isApiFirstRouteTrackKey)
    .map(key => parseRowFromTrack(spec, key))
    .filter((row): row is ParsedRouteRow => row !== null);

  if (rows.length === 0) {
    return null;
  }

  type MethodEntry = { id: string; method: string; summary: string };

  const byPath = rows.reduce((map, row) => {
    const list: MethodEntry[] = map.get(row.path) ?? [];
    list.push({
      id: row.methodId,
      method: row.method,
      summary: ''
    });
    map.set(row.path, list);
    return map;
  }, new Map<string, MethodEntry[]>());

  const { orderedPaths } = rows.reduce(
    (acc, row) => {
      if (acc.seen.has(row.path)) {
        return acc;
      }
      acc.seen.add(row.path);
      acc.orderedPaths.push(row.path);
      return acc;
    },
    { seen: new Set<string>(), orderedPaths: [] as string[] }
  );

  const routes: ApiRoutes[] = orderedPaths
    .map(path => {
      const methods = byPath.get(path);
      if (!methods?.length) {
        return null;
      }
      return {
        path,
        methods: methods.map(m => ({
          id: m.id,
          method: m.method,
          summary: m.summary,
          responses: []
        }))
      };
    })
    .filter((r): r is ApiRoutes => r !== null);

  return routes;
}
